import {
  FinanceCategoryScope,
  FinanceFundKind,
  FinanceFundStatus,
  type CreateFinanceFundInput,
  type CreateFinanceSetupFundInput,
  type FinanceFundRecord,
  type ListFinanceFundsQuery,
  type ReallocateFundsInput,
  type UpdateFinanceFundInput,
} from "@/features/finance/model/finance-types"
import {
  http,
} from "@/shared/lib/api/http"
import { RATIO_DISPLAY_CURRENCY } from "@/features/finance/overview/lib/overview-format"
import {
  ZERO_MONEY,
  normalizeCurrencyCode,
  normalizeMoney,
  normalizeNullableMoney,
  normalizeNullableString,
  normalizeNumber,
  normalizeString,
} from "./finance-api-shared"


function normalizeFinanceFundRecord(value: unknown): FinanceFundRecord {
  const record = (value ?? {}) as Record<string, unknown>
  const branchRecord = (record.branch ?? {}) as Record<string, unknown>
  const rawStatus = normalizeString(record.status)
  const status =
    rawStatus === FinanceFundStatus.Closed
      ? FinanceFundStatus.Closed
      : rawStatus === FinanceFundStatus.Archived
        ? FinanceFundStatus.Archived
        : FinanceFundStatus.Active

  return {
    id: normalizeString(record.id) ?? "",
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? "",
    branchId: normalizeString(record.branchId ?? record.branch_id) ?? "",
    // The parse boundary is where a stored name becomes a `string`; from here on the type keeps it out of
    // markup until something resolves it.
    name: normalizeString(record.name) ?? "",
    nameTranslationKey: normalizeNullableString(record.nameTranslationKey ?? record.name_translation_key),
    description: normalizeNullableString(record.description),
    status,
    kind:
      (record.kind ?? record.fund_kind) === FinanceFundKind.SystemLinked
        ? FinanceFundKind.SystemLinked
        : FinanceFundKind.Custom,
    linkedIncomeCategoryId: normalizeNullableString(record.linkedIncomeCategoryId ?? record.linked_income_category_id),
    linkedIncomeAccountId: normalizeNullableString(record.linkedIncomeAccountId ?? record.linked_income_account_id),
    scope: record.scope === FinanceCategoryScope.Parish ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch,
    targetAmount: normalizeNullableMoney(record.targetAmount ?? record.target_amount),
    startDate: normalizeNullableString(record.startDate ?? record.start_date),
    endDate: normalizeNullableString(record.endDate ?? record.end_date),
    isRestricted: Boolean(record.isRestricted ?? record.is_restricted),
    restrictionStartDate: normalizeNullableString(record.restrictionStartDate ?? record.restriction_start_date),
    restrictionEndDate: normalizeNullableString(record.restrictionEndDate ?? record.restriction_end_date),
    allowedExpenseAccountIds: Array.isArray(record.allowedExpenseAccountIds)
      ? (record.allowedExpenseAccountIds as unknown[]).map((id) => String(id))
      : [],
    // The scale `targetAmount` and `balance` are denominated at, as the API sent it (IBA-116) — never guessed.
    currency: normalizeCurrencyCode(record.currency) ?? null,
    balance: normalizeMoney(record.balance) ?? ZERO_MONEY,
    reserved: normalizeMoney(record.reserved) ?? ZERO_MONEY,
    // Falls back to the balance when the API predates this field: a fund with no known holds has all of its
    // balance available, which is exactly what the client assumed before.
    available: normalizeMoney(record.available) ?? normalizeMoney(record.balance) ?? ZERO_MONEY,
    activityCount: normalizeNumber(record.activityCount ?? record.activity_count) ?? 0,
    dedicatedActivityId: normalizeNullableString(record.dedicatedActivityId ?? record.dedicated_activity_id),
    dedicatedActivityName: normalizeNullableString(record.dedicatedActivityName ?? record.dedicated_activity_name),
    branch: {
      id: normalizeString(branchRecord.id) ?? "",
      workspaceId: normalizeString(branchRecord.workspaceId ?? branchRecord.workspace_id) ?? "",
      name: normalizeString(branchRecord.name) ?? "",
      slug: normalizeString(branchRecord.slug) ?? "",
      status: normalizeString(branchRecord.status) ?? "ACTIVE",
    },
    createdAt: normalizeString(record.createdAt ?? record.created_at) ?? new Date().toISOString(),
    updatedAt: normalizeString(record.updatedAt ?? record.updated_at) ?? new Date().toISOString(),
  }
}


export async function listFinanceFunds(query: ListFinanceFundsQuery): Promise<{
  items: FinanceFundRecord[]
  total: number
  page: number
  limit: number
}> {
  const response = await http.get<unknown>("/finance/funds", {
    params: {
      workspaceId: query.workspaceId,
      ...(typeof query.branchId !== "undefined" ? { branchId: query.branchId } : {}),
      ...(query.scope ? { scope: query.scope } : {}),
      ...(query.status ? { status: query.status } : {}),
      ...(query.kind ? { kind: query.kind } : {}),
      ...(query.search ? { search: query.search } : {}),
      ...(typeof query.page === "number" ? { page: query.page } : {}),
      ...(typeof query.limit === "number" ? { limit: Math.min(query.limit, 100) } : {}),
      ...(query.sortBy ? { sortBy: query.sortBy } : {}),
      ...(query.sortOrder ? { sortOrder: query.sortOrder } : {}),
    },
  })

  const record = (response.data ?? {}) as Record<string, unknown>
  const rows = Array.isArray(record.items) ? record.items.map(normalizeFinanceFundRecord) : []

  /*
   * Branch autonomy (ADR-0024) sends every fund figure as a SHARE of its group's total rather than an amount,
   * and says so with `figuresAreRatios`. Swapping the row's currency for the ratio sentinel HERE means every
   * fund surface — the funds page, the transaction form's split card, pledges, budgets — formats percentages
   * without one of them having to know what governance is.
   *
   * The alternative was a flag threaded through each of them, where the ones that forgot would print a share
   * with a dollar sign in front of it.
   */
  const items = record.figuresAreRatios === true
    ? rows.map((row) => ({ ...row, currency: RATIO_DISPLAY_CURRENCY as FinanceFundRecord["currency"] }))
    : rows

  return {
    items,
    total: normalizeNumber(record.total) ?? items.length,
    page: normalizeNumber(record.page) ?? 1,
    limit: normalizeNumber(record.limit) ?? Math.max(items.length, 1),
  }
}


export async function createFinanceFund(input: CreateFinanceFundInput): Promise<FinanceFundRecord> {
  const response = await http.post<unknown>("/finance/funds", {
    workspaceId: input.workspaceId,
    ...(typeof input.branchId !== "undefined" ? { branchId: input.branchId } : {}),
    ...(input.scope ? { scope: input.scope } : {}),
    name: input.name,
    description: input.description ?? null,
    ...(typeof input.targetAmount !== "undefined" ? { targetAmount: input.targetAmount } : {}),
    ...(typeof input.startDate !== "undefined" ? { startDate: input.startDate } : {}),
    ...(typeof input.endDate !== "undefined" ? { endDate: input.endDate } : {}),
    ...(typeof input.isRestricted !== "undefined" ? { isRestricted: input.isRestricted } : {}),
    ...(typeof input.restrictionStartDate !== "undefined" ? { restrictionStartDate: input.restrictionStartDate } : {}),
    ...(typeof input.restrictionEndDate !== "undefined" ? { restrictionEndDate: input.restrictionEndDate } : {}),
    ...(typeof input.allowedExpenseAccountIds !== "undefined" ? { allowedExpenseAccountIds: input.allowedExpenseAccountIds } : {}),
    ...(input.dedicateToActivityId ? { dedicateToActivityId: input.dedicateToActivityId } : {}),
  })

  return normalizeFinanceFundRecord(response.data)
}

export async function createFinanceSetupFund(input: CreateFinanceSetupFundInput): Promise<FinanceFundRecord> {
  const response = await http.post<unknown>("/finance/funds/setup", {
    workspaceId: input.workspaceId,
    ...(typeof input.branchId !== "undefined" ? { branchId: input.branchId } : {}),
    ...(input.scope ? { scope: input.scope } : {}),
    name: input.name,
    description: input.description ?? null,
  })

  return normalizeFinanceFundRecord(response.data)
}


/**
 * Dedicates a fund to one activity, or releases it when `activityId` is null (IBA-175).
 *
 * Releasing removes the restriction only — never the fund, and never the activity's own preferred fund.
 */
export async function setFinanceFundDedication(input: {
  id: string
  activityId: string | null
}): Promise<FinanceFundRecord> {
  const response = await http.put<unknown>(`/finance/funds/${input.id}/dedication`, {
    activityId: input.activityId,
  })

  return normalizeFinanceFundRecord(response.data)
}


/**
 * IBA-154 (D9): move a designation between funds. No physical money moves — the cash stays in whatever account
 * holds it and income history is untouched; only what the money is FOR changes.
 */
export async function reallocateFunds(input: ReallocateFundsInput): Promise<{ sourceFundId: string; destinationFundId: string }> {
  const response = await http.post<unknown>("/finance/funds/reallocate", {
    workspaceId: input.workspaceId,
    ...(typeof input.branchId !== "undefined" ? { branchId: input.branchId } : {}),
    ...(input.scope ? { scope: input.scope } : {}),
    sourceFundId: input.sourceFundId,
    destinationFundId: input.destinationFundId,
    amount: input.amount,
  })
  const record = (response.data ?? {}) as Record<string, unknown>
  return {
    sourceFundId: normalizeString(record.sourceFundId) ?? input.sourceFundId,
    destinationFundId: normalizeString(record.destinationFundId) ?? input.destinationFundId,
  }
}


export async function updateFinanceFund(input: UpdateFinanceFundInput): Promise<FinanceFundRecord> {
  const response = await http.patch<unknown>(`/finance/funds/${input.id}`, {
    ...(typeof input.patch.name !== "undefined" ? { name: input.patch.name } : {}),
    ...(typeof input.patch.description !== "undefined" ? { description: input.patch.description } : {}),
    ...(typeof input.patch.targetAmount !== "undefined" ? { targetAmount: input.patch.targetAmount } : {}),
    ...(typeof input.patch.startDate !== "undefined" ? { startDate: input.patch.startDate } : {}),
    ...(typeof input.patch.endDate !== "undefined" ? { endDate: input.patch.endDate } : {}),
    ...(typeof input.patch.isRestricted !== "undefined" ? { isRestricted: input.patch.isRestricted } : {}),
    ...(typeof input.patch.restrictionStartDate !== "undefined" ? { restrictionStartDate: input.patch.restrictionStartDate } : {}),
    ...(typeof input.patch.restrictionEndDate !== "undefined" ? { restrictionEndDate: input.patch.restrictionEndDate } : {}),
    ...(typeof input.patch.allowedExpenseAccountIds !== "undefined" ? { allowedExpenseAccountIds: input.patch.allowedExpenseAccountIds } : {}),
  })

  return normalizeFinanceFundRecord(response.data)
}


export async function setFinanceFundStatus(input: {
  id: string
  status: FinanceFundRecord["status"]
}): Promise<FinanceFundRecord> {
  const response = await http.post<unknown>(`/finance/funds/${input.id}/status`, {
    status: input.status,
  })

  return normalizeFinanceFundRecord(response.data)
}


/** Attaches the given finance activities to a fund (sets each activity's fundId to this fund). */
export async function attachFinanceFundActivities(input: {
  id: string
  activityIds: string[]
}): Promise<{ id: string; attached: number }> {
  const response = await http.post<unknown>(`/finance/funds/${input.id}/activities`, {
    activityIds: input.activityIds,
  })

  const record = (response.data ?? {}) as Record<string, unknown>
  return {
    id: normalizeString(record.id) ?? input.id,
    attached: normalizeNumber(record.attached) ?? 0,
  }
}
